// The japanese style's band (docs/AUDIO_DESIGN.md §12): the track's Japanese scale (the lead's notes are mapped into it),
// a shakuhachi (breathy bamboo flute with meri dips, nayashi bends and a late, deepening vibrato), a soft round bass and a
// soft boom-bap kit with a mokugyo wood block, a taiko, a wooden clack and a small rin bell. Persistent voices gated with
// setTargetAtTime, like instruments.js: nothing is allocated per note or hit.
import { clamp } from '../../engine/core.js';
import { mtof, hold, noise } from '../dsp.js';
import { PENTA } from '../harmony.js';
import { LEVELS, gain, filter, osc, panner, persistent } from '../instruments.js';

// Levels (tuning knobs for the mix balance), against the jazzhop bass, lead and kit they replace.
export const JP = { bass: 0.56, lead: 1.7, kit: 1.12 };
const mod12 = n => ((n % 12) + 12) % 12;

/* ---------------- scales: the melody's pentatonic, mapped degree for degree ---------------- */
// in (miyako-bushi, the koto's hirajōshi tuning) 1 ♭2 4 5 ♭6 and in-sen 1 ♭2 4 5 ♭7 in minor keys; yo 1 2 4 5 6 or the
// plain major pentatonic in major. The engine writes the lead in the key's pentatonic (major 1 2 3 5 6, minor 1 ♭3 4 5 ♭7);
// each of its 5 degrees goes to the scale's degree of the same rank (in: ♭3 → ♭2, ♭7 → ♭6; yo: 3 → 4), so the contour,
// the octave and the tonic stay and the style's chords (sus, open fifths, ♭II, iv, no minor or major 3rd of the key where
// the map moves it) take every mapped chord tone.
export const SCALES = { in: [0, 1, 5, 7, 8], insen: [0, 1, 5, 7, 10], yo: [0, 2, 5, 7, 9], major: [0, 2, 4, 7, 9] };
/** a pentatonic note of the key → the same degree of the track's scale (notes outside the pentatonic stay) */
export function toScale(midi, key, scale) {
  const d = mod12(midi - key.tonic), k = PENTA[key.mode].indexOf(d), S = SCALES[scale];
  return k < 0 || !S ? midi : midi - d + S[k];
}
/** m → is it in the track's scale */
export const scaleOf = (key, scale) => m => (SCALES[scale] || SCALES.major).includes(mod12(m - key.tonic));
/** meri notes (the shaded, half-holed ones): the ♭2 and ♭6 of in / in-sen */
const isMeri = (m, key, scale) => (scale === 'in' || scale === 'insen') && [1, 8].includes(mod12(m - key.tonic));

/* ---------------- shakuhachi ---------------- */
// the bore's tone: a sine plus a triangle's odd harmonics at .12 (−1/9, 1/25, −1/49 …), one oscillator
const WAVES = new WeakMap();
function boreWave(ac) {
  if (!WAVES.has(ac)) {
    const n = 12, re = new Float32Array(n), im = new Float32Array(n);
    im[1] = 1 + 0.12 * 8 / (Math.PI * Math.PI);                                   // the sine + the triangle's own fundamental
    for (let k = 3; k < n; k += 2) im[k] = 0.12 * (8 / (Math.PI * Math.PI)) * ((k - 1) / 2 % 2 ? -1 : 1) / (k * k);
    WAVES.set(ac, ac.createPeriodicWave(re, im, { disableNormalization: true }));
  }
  return WAVES.get(ac);
}
/**
 * createShakuhachi(ac, out, send, plan, opts) → { note, dispose }   plan.japanese: { scale, vib, rate, nayashi, orn, echo }
 * Tone: sine f + a triangle's odd harmonics at .12 (one PeriodicWave) → toneAmp; breath: noise → BP 1.7·f (Q .6, 900–3600 Hz) → breathAmp, both → LP 2.7 kHz
 * (meri notes ×.7) → trem → pan. A new note (not slurred) re-blows: the tone dips to .25 (τ 10 ms) and swells back with
 * τ 45 ms (30 ms above MIDI 74); the breath bursts to 3.5 for 60 ms (muraiki, ≈ 11 dB under the tone, p .35 and on every
 * phrase start) or 2, then holds 1.1 (≈ 21 dB under; meri ×1.3; a slur 1.4). Pitch: notes ≥ .5 s start flat by 50–90 ¢ and rise over τ 120 ms
 * (nayashi, p nayashi), else a 15 ¢ scoop (τ 30 ms); slurs slide (τ 50 ms, suri). Vibrato rate 4.4–5.4 Hz, from 0 to
 * ±vib ¢ (14–24) starting 350 ms in with τ max(.25, .45·d): it deepens late in the note, with ±6 % breath flutter. Notes
 * over 1.1 s sag 25 ¢ as they fade (τ 90 ms tone, 140 ms breath: a breathy tail). Grace notes in the scale before notes
 * ≥ .2 s: a passing note over a 3rd within .35 s (p .5), else a cut (the upper neighbour, 70 %) or a tap, p orn, 45–70 ms;
 * a repeated pitch is re-blown with a finger strike (atari: 35 ms on the scale tone above), never slurred.
 * Echo 3 or 4 steps (plan.japanese.echo), fb .3, LP 1.9k, wet .24, pan lead.pan, reverb send .6.
 */
export function createShakuhachi(ac, out, send, plan, { registry, tag, stepDur, r, t0 = ac.currentTime }) {
  const lead = plan.lead, J = plan.japanese, key = plan.key, inScale = scaleOf(key, J.scale);
  const nodes = [], N = x => (nodes.push(x), x);
  const o1 = N(osc(ac, 'sine', 440)), toneAmp = N(gain(ac, 0));
  o1.setPeriodicWave(boreWave(ac));
  const src = N(ac.createBufferSource()), bp = N(filter(ac, 'bandpass', 1500, 0.6)), breathAmp = N(gain(ac, 0));
  src.buffer = noise(ac, 'white'); src.loop = true;
  const lp = N(filter(ac, 'lowpass', 2700, 0.5)), trem = N(gain(ac, 1)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.6));
  const dly = N(ac.createDelay(2)), dLP = N(filter(ac, 'lowpass', 1900, 0.5)), fb = N(gain(ac, 0.3)), wet = N(gain(ac, 0.24));
  const vib = N(osc(ac, 'sine', J.rate || 5)), vibG = N(gain(ac, 0)), tremG = N(gain(ac, 0));
  dly.delayTime.value = Math.min(1.9, (J.echo || 3) * stepDur);
  o1.connect(toneAmp); src.connect(bp); bp.connect(breathAmp);
  toneAmp.connect(lp); breathAmp.connect(lp); lp.connect(trem); trem.connect(pan); trem.connect(dly);
  dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(pan);
  pan.connect(out); pan.connect(sendG); sendG.connect(send);
  vib.connect(vibG); vibG.connect(o1.detune); vib.connect(tremG); tremG.connect(trem.gain);
  const p = persistent(registry, tag, [o1, src, vib], nodes, t0, 2.1);
  let last = null, off = -1;
  const setF = (hz, at, tau) => { if (tau) o1.frequency.setTargetAtTime(hz, at, tau); else o1.frequency.setValueAtTime(hz, at); };
  return {
    note(t, midi, vel, dur, glide = false) {
      const m = toScale(midi, key, J.scale), d = Math.max(0.08, dur), f = mtof(m), A = 0.2 * vel * JP.lead, prev = last;
      const meri = isMeri(m, key, J.scale), fresh = !prev || t - prev.end > 0.25;
      last = { midi: m, end: t + d };
      for (const prm of [o1.frequency, toneAmp.gain, breathAmp.gain, vibG.gain, tremG.gain, lp.frequency]) hold(prm, t);
      // a repeated pitch is articulated with a finger strike (atari: 35 ms on the scale tone above), never slurred into
      const same = !!prev && prev.midi === m && t - prev.end < 0.3, g = same ? atari(m, inScale) : glide ? null : graceNote(prev, t, m, d, r, J.orn, inScale);
      if (same) glide = false;
      lp.frequency.setTargetAtTime(meri ? 1900 : 2700, t, 0.03);
      bp.frequency.setValueAtTime(clamp(1.7 * f, 900, 3600), t);
      if (g) { setF(mtof(g.m), t, 0); setF(f, t + g.gd, 0.008); }
      else if (glide) setF(f, t, 0.05);                                       // suri: a slide into the note
      else if (d >= 0.5 && r() < J.nayashi) { setF(f * Math.pow(2, -(50 + 40 * r()) / 1200), t, 0); setF(f, t + 0.04, 0.12); }
      else { setF(f * Math.pow(2, -15 / 1200), t, 0); setF(f, t, 0.03); }
      const bk = meri ? 1.3 : 1;
      if (glide) { toneAmp.gain.setTargetAtTime(A, t, 0.04); breathAmp.gain.setTargetAtTime(1.4 * A * bk, t, 0.02); }
      else {
        if (t < off) toneAmp.gain.setTargetAtTime(0.25 * A, t, 0.01);        // the breath stops for a moment
        toneAmp.gain.setTargetAtTime(A, t + (t < off ? 0.015 : 0), m > 74 ? 0.03 : 0.045);
        breathAmp.gain.setTargetAtTime((fresh || r() < 0.35 ? 3.5 : 2) * A * bk, t, 0.005);   // muraiki
      }
      breathAmp.gain.setTargetAtTime(1.1 * A * bk, t + 0.06, 0.06);
      vibG.gain.setTargetAtTime(0, t, 0.03); tremG.gain.setTargetAtTime(0, t, 0.03);
      if (d > 0.5) { const tau = Math.max(0.25, 0.45 * d); vibG.gain.setTargetAtTime(J.vib, t + 0.35, tau); tremG.gain.setTargetAtTime(0.06, t + 0.35, tau); }
      if (d > 1.1) setF(f * Math.pow(2, -25 / 1200), t + d - 0.04, 0.12);    // the fall at the end of a long note
      toneAmp.gain.setTargetAtTime(0, t + d, 0.09);
      breathAmp.gain.setTargetAtTime(0, t + d + 0.02, 0.14);
      off = t + d;
    },
    dispose(t) { p.dispose(t); },
  };
}

/** the finger strike that re-articulates a repeated note: the scale's next tone above, 35 ms → { m, gd } */
function atari(midi, inScale) {
  for (let k = midi + 1; k <= midi + 4; k++) if (inScale(k)) return { m: k, gd: 0.035 };
  return { m: midi + 2, gd: 0.035 };
}

/** The grace note before a melody note of d s at t, or null: a passing note of the scale when the melody moves a 3rd within
 *  .35 s of the last note (p .5, 50–85 ms), else with p orn a cut (the scale's upper neighbour, 70 %) or a tap (the
 *  lower), 45–70 ms; only before notes of at least .2 s. prev: { midi, end } | null. → { m, gd } | null */
export function graceNote(prev, t, midi, d, r, orn, inScale) {
  if (d < 0.2) return null;
  const iv = prev ? Math.abs(midi - prev.midi) : 0;
  if (prev && t - prev.end < 0.35 && iv >= 3 && iv <= 5) {
    const c = [];
    for (let k = Math.min(prev.midi, midi) + 1; k < Math.max(prev.midi, midi); k++) if (inScale(k)) c.push(k);
    if (c.length === 1 && r() < 0.5) return { m: c[0], gd: clamp(0.25 * d, 0.05, 0.085) };
  }
  if (r() < orn) {
    const dir = r() < 0.7 ? 1 : -1;
    for (let k = midi + dir; Math.abs(k - midi) <= 4; k += dir) if (inScale(k)) return { m: k, gd: clamp(0.2 * d, 0.045, 0.07) };
  }
  return null;
}

/* ---------------- bass: soft and round ---------------- */
/** sine f + triangle f (.16) → LP p.lp (420–520 Hz, Q .6) → amp. Attack τ 10 ms to .46v, settling to .7 of it (τ .45 s),
 *  release τ 70 ms; a slide comes up from −100 ¢ (τ 25 ms). The triangle's 3rd harmonic carries it on phones. p: { lp } */
export function createWaBass(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const sine = osc(ac, 'sine', 55), tri = osc(ac, 'triangle', 55), triG = gain(ac, 0.16), lp = filter(ac, 'lowpass', p.lp || 470, 0.6), amp = gain(ac, 0);
  sine.connect(lp); tri.connect(triG); triG.connect(lp); lp.connect(amp); amp.connect(out);
  const v = persistent(registry, tag, [sine, tri], [sine, tri, triG, lp, amp], t0);
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), A = 0.46 * vel * JP.bass;
      for (const o of [sine, tri]) {
        hold(o.frequency, t);
        if (slide) { o.frequency.setValueAtTime(f * Math.pow(2, -100 / 1200), t); o.frequency.setTargetAtTime(f, t, 0.025); }
        else o.frequency.setValueAtTime(f, t);
      }
      hold(amp.gain, t);
      amp.gain.setTargetAtTime(A, t, 0.01);
      amp.gain.setTargetAtTime(0.7 * A, t + 0.04, 0.45);
      amp.gain.setTargetAtTime(0, t + Math.max(0.08, dur), 0.07);
    },
    dispose(t) { v.dispose(t); },
  };
}

/* ---------------- kit: soft boom-bap, mokugyo, taiko, wooden clack, rin (one noise source) ---------------- */
/** kit: { kickF0, kickF1, kickDecay, snareBP, snareDecay, hatHP, hatClosed, rimF, blockF, blockDecay, taikoF0, taikoF1,
 *  taikoDecay, rinF, lp }. The voices share one noise source and four pan/reverb taps: snare + clack (−.1, reverb .24),
 *  taiko (centre, .28), hat (+.2, .06), mokugyo + rin (+.2, .34); the kick goes to the bus dry.
 *  kick = a soft felt kick: sine kickF0 (110–130) → kickF1 (46–52) Hz (τ 32 ms), .5v, attack τ 1.5 ms, decay τ kickDecay
 *         (.14–.2 s) + a felt thump (noise → BP 320 Hz, Q .8, .1v, τ 14 ms), no click
 *  snare = soft, half brushed: noise → BP snareBP (1.6–2.2 kHz, Q .7), .36v, τ snareDecay (.08–.12 s; ghost 30 ms) + the
 *         body (triangle 205 → 175 Hz, .18v, τ 35 ms)
 *  hat = noise → HP hatHP (6.4–8 kHz), .11v, closed τ hatClosed (11–16 ms), open τ .12 s
 *  rim = a hyoshigi-like wooden clack: sine rimF (950–1250 Hz) .13v + noise BP 2.8 kHz (Q 1.2) .1v, τ 9 ms
 *  block = the mokugyo (a temple's hollow wooden fish): sine blockF (520–720 Hz snapped to the key, falling 4 % in τ 10 ms)
 *         + sine 2.46·f (.28, τ 25 ms), .26v, τ blockDecay (55–80 ms) + a knock (noise BP 2.2 kHz, .07v, τ 4 ms)
 *  tom = a taiko, soft bachi: sine taikoF0 (118–135) → taikoF1 (66–78) Hz (τ 70 ms), .5v, decay τ taikoDecay (.34–.48 s)
 *         + the head (noise → BP 170 Hz, Q .9, .16v, τ 45 ms)
 *  shaker = the rin (a small bowl bell): sines rinF (2.3–2.8 kHz, snapped to the key) and 2.71·rinF, .045v and .02v,
 *         τ .7 and .35 s */
export function createWaKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime }) {
  const L = LEVELS.kit * JP.kit, nodes = [], N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  const tap = (p, s) => { const pn = N(panner(ac, p)), sg = N(gain(ac, s)); pn.connect(bus); pn.connect(sg); sg.connect(send); return pn; };
  const tS = tap(-0.1, 0.24), tT = tap(0, 0.28), tH = tap(0.2, 0.06), tW = tap(0.2, 0.34);
  const chain = (...xs) => xs.forEach((x, k) => k && xs[k - 1].connect(x));
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0)), kBP = N(filter(ac, 'bandpass', 320, 0.8)), kThud = N(gain(ac, 0));
  chain(kOsc, kAmp, bus); chain(src, kBP, kThud, bus);
  const sBP = N(filter(ac, 'bandpass', kit.snareBP, 0.7)), sAmp = N(gain(ac, 0)), sTri = N(osc(ac, 'triangle', 190)), sTone = N(gain(ac, 0));
  chain(src, sBP, sAmp, tS); chain(sTri, sTone, tS);
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.7)), hAmp = N(gain(ac, 0));
  chain(src, hHP, hAmp, tH);
  const r1 = N(osc(ac, 'sine', kit.rimF)), rAmp = N(gain(ac, 0)), rBP = N(filter(ac, 'bandpass', 2800, 1.2)), rNz = N(gain(ac, 0));
  chain(r1, rAmp, tS); chain(src, rBP, rNz, tS);
  const b1 = N(osc(ac, 'sine', kit.blockF)), b2 = N(osc(ac, 'sine', 2.46 * kit.blockF)), bA1 = N(gain(ac, 0)), bA2 = N(gain(ac, 0));
  const bBP = N(filter(ac, 'bandpass', 2200, 1)), bKnock = N(gain(ac, 0));
  chain(b1, bA1, tW); chain(b2, bA2, tW); chain(src, bBP, bKnock, tW);
  const tOsc = N(osc(ac, 'sine', kit.taikoF1)), tAmp = N(gain(ac, 0)), tBP = N(filter(ac, 'bandpass', 170, 0.9)), tHead = N(gain(ac, 0));
  chain(tOsc, tAmp, tT); chain(src, tBP, tHead, tT);
  const c1 = N(osc(ac, 'sine', kit.rinF)), c2 = N(osc(ac, 'sine', 2.71 * kit.rinF)), cA1 = N(gain(ac, 0)), cA2 = N(gain(ac, 0));
  chain(c1, cA1, tW); chain(c2, cA2, tW);
  const p = persistent(registry, tag, [src, kOsc, sTri, r1, b1, b2, tOsc, c1, c2], nodes, t0, 1.4);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.032);
      hit(kAmp.gain, 0.5 * v * L, t, 0.0015, 0.008, kit.kickDecay);
      hit(kThud.gain, 0.1 * v * L, t, 0.002, 0.004, 0.014);
    },
    snare(t, v, ghost = false) {
      sTri.frequency.setValueAtTime(205, t); sTri.frequency.setTargetAtTime(175, t, 0.03);
      hit(sAmp.gain, 0.36 * v * L, t, 0.0012, 0.004, ghost ? 0.03 : kit.snareDecay);
      hit(sTone.gain, 0.18 * v * L, t, 0.001, 0.004, 0.035);
    },
    hat(t, v, open = false) { hit(hAmp.gain, 0.11 * v * L, t, 0.0008, 0.003, open ? 0.12 : kit.hatClosed); },
    rim(t, v) { hit(rAmp.gain, 0.13 * v * L, t, 0.0006, 0.002, 0.009); hit(rNz.gain, 0.1 * v * L, t, 0.0005, 0.001, 0.007); },
    block(t, v) {
      b1.frequency.setValueAtTime(1.04 * kit.blockF, t); b1.frequency.setTargetAtTime(kit.blockF, t, 0.01);
      hit(bA1.gain, 0.26 * v * L, t, 0.0008, 0.003, kit.blockDecay);
      hit(bA2.gain, 0.073 * v * L, t, 0.0008, 0.002, 0.025);
      hit(bKnock.gain, 0.07 * v * L, t, 0.0005, 0.001, 0.004);
    },
    tom(t, v) {
      tOsc.frequency.setValueAtTime(kit.taikoF0, t); tOsc.frequency.setTargetAtTime(kit.taikoF1, t, 0.07);
      hit(tAmp.gain, 0.5 * v * L, t, 0.002, 0.01, kit.taikoDecay);
      hit(tHead.gain, 0.16 * v * L, t, 0.002, 0.006, 0.045);
    },
    shaker(t, v) { hit(cA1.gain, 0.045 * v * L, t, 0.001, 0.004, 0.7); hit(cA2.gain, 0.02 * v * L, t, 0.001, 0.003, 0.35); },
    dispose(t) { p.dispose(t); },
  };
}
